import React, { useState } from 'react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { capabilities, errMsg } from '../lib/roles';

const SOCIAL = ['instagram', 'tiktok', 'facebook'];
const MUSIC = ['soundcloud', 'spotify', 'mixcloud', 'youtube'];
const inputCls = 'w-full bg-[#111] border border-gray-700 text-white px-3 py-2 rounded text-sm focus:border-red-500 outline-none';

/**
 * Bio, social profiles and (for DJs) music links shown on the public profile
 * and promoter dashboard. Only handles/links — never social-media passwords.
 */
export default function PublicProfileCard() {
  const { user, checkAuth } = useAuth();
  const caps = capabilities(user);
  const isDj = caps.includes('dj') || user?.account_type === 'dj';
  const [bio, setBio] = useState(user?.bio || '');
  const [social, setSocial] = useState(() => Object.fromEntries(SOCIAL.map(k => [k, user?.social_links?.[k] || ''])));
  const [music, setMusic] = useState(() => Object.fromEntries(MUSIC.map(k => [k, user?.music_links?.[k] || ''])));
  const [saving, setSaving] = useState(false);

  if (!user || caps.length === 0) return null;

  const save = async () => {
    setSaving(true);
    try {
      await authAPI.updateProfile({ bio: bio.trim() || null, socialLinks: social, ...(isDj ? { musicLinks: music } : {}) });
      toast.success('Public profile saved');
      checkAuth?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6 mb-6">
      <h2 className="text-white font-bold text-sm uppercase tracking-wide mb-1">Public profile</h2>
      <p className="text-gray-500 text-xs mb-4">Shown to organisers and attendees. Add handles or links only — SAC Events never asks for your passwords.</p>
      <label className="block text-gray-400 text-xs mb-1">Bio</label>
      <textarea className={inputCls} rows={4} maxLength={2000} value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell people about yourself" />
      <p className="text-gray-400 text-xs mt-4 mb-1">Social profiles</p>
      <div className="grid sm:grid-cols-3 gap-2">
        {SOCIAL.map(k => (
          <input key={k} className={inputCls} value={social[k]} onChange={e => setSocial(p => ({ ...p, [k]: e.target.value }))} placeholder={k[0].toUpperCase() + k.slice(1)} />
        ))}
      </div>
      {isDj && (
        <>
          <p className="text-gray-400 text-xs mt-4 mb-1">Music links</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {MUSIC.map(k => (
              <input key={k} className={inputCls} value={music[k]} onChange={e => setMusic(p => ({ ...p, [k]: e.target.value }))} placeholder={`${k[0].toUpperCase() + k.slice(1)} URL`} />
            ))}
          </div>
        </>
      )}
      <button onClick={save} disabled={saving} className="btn-primary mt-4 px-5 py-2 rounded text-sm font-bold disabled:opacity-60">
        {saving ? 'Saving…' : 'Save public profile'}
      </button>
    </div>
  );
}
